import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Form, InputGroup, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { add_product, get_all_products, update_product_field } from '../../../utils/services';
import './Admin_panel.css';
import './Admin_products.css';

const PRODUCT_COLUMNS = ['id', 'name', 'description', 'exw_price', 'cost', 'prod_status'];
const NON_EDITABLE_FIELDS = new Set(['id']);
const NUMERIC_FIELDS = new Set(['exw_price', 'cost', 'prod_status']);
const DESCRIPTION_PREVIEW_MAX = 80;

function truncateDescription(text, max = DESCRIPTION_PREVIEW_MAX) {
  const value = String(text ?? '');
  if (value.length <= max) {
    return value;
  }
  return `${value.slice(0, max)}…`;
}

const EMPTY_FORM = {
  name: '',
  description: '',
  exw_price: '0',
  cost: '0',
  prod_status: '1',
};

function matchesSearch(product, term) {
  if (!term) {
    return true;
  }
  const normalized = term.toLowerCase();
  return Object.values(product).some((value) => {
    if (value == null) {
      return false;
    }
    return String(value).toLowerCase().includes(normalized);
  });
}

function cellKey(productId, field) {
  return `${productId}:${field}`;
}

function Admin_products() {
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingCell, setSavingCell] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newProduct, setNewProduct] = useState(EMPTY_FORM);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await get_all_products(token);
      if (result === 'INVALID') {
        navigate('/');
        return;
      }
      if (!Array.isArray(result)) {
        setProducts([]);
        setError('No se pudieron cargar los productos.');
        return;
      }
      setProducts(result);
    } catch (err) {
      setError('Error al cargar los productos.');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const filteredProducts = useMemo(
    () => products.filter((product) => matchesSearch(product, searchTerm.trim())),
    [products, searchTerm]
  );

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const startEdit = (product, field) => {
    if (NON_EDITABLE_FIELDS.has(field) || savingCell) {
      return;
    }
    setEditingCell({ productId: product.id, field });
    setEditValue(product[field] ?? '');
  };

  const saveEdit = async (product, field) => {
    const currentValue = product[field] ?? '';
    const nextValue = editValue;

    if (String(currentValue) === String(nextValue)) {
      cancelEdit();
      return;
    }

    const savingKey = cellKey(product.id, field);
    setSavingCell(savingKey);

    try {
      const result = await update_product_field(token, product.id, field, nextValue);
      if (result === 'INVALID') {
        navigate('/');
        return;
      }
      if (result !== 'OK') {
        setError(`No se pudo actualizar ${field}.`);
        cancelEdit();
        return;
      }

      setProducts((prev) =>
        prev.map((row) =>
          row.id === product.id ? { ...row, [field]: nextValue } : row
        )
      );
      setError('');
      cancelEdit();
    } catch (err) {
      setError(`Error al actualizar ${field}.`);
      cancelEdit();
    } finally {
      setSavingCell(null);
    }
  };

  const closeCreateModal = () => {
    if (creating) {
      return;
    }
    setShowCreateModal(false);
    setNewProduct(EMPTY_FORM);
  };

  const handleCreate = async () => {
    const name = newProduct.name.trim();
    const description = newProduct.description.trim();

    if (!name || !description) {
      setError('Nombre y descripción son obligatorios.');
      return;
    }

    setCreating(true);
    setError('');

    try {
      const result = await add_product(token, {
        name,
        description,
        exw_price: parseFloat(newProduct.exw_price) || 0,
        cost: parseInt(newProduct.cost, 10) || 0,
        prod_status: parseInt(newProduct.prod_status, 10) || 0,
      });

      if (result === 'INVALID') {
        navigate('/');
        return;
      }
      if (!result || typeof result !== 'object' || !result.id) {
        setError('No se pudo crear el producto.');
        return;
      }

      setProducts((prev) => [...prev, result].sort((a, b) =>
        String(a.name).localeCompare(String(b.name))
      ));
      setShowCreateModal(false);
      setNewProduct(EMPTY_FORM);
    } catch (err) {
      setError('Error al crear el producto.');
    } finally {
      setCreating(false);
    }
  };

  const renderCell = (product, col) => {
    const isEditable = !NON_EDITABLE_FIELDS.has(col);
    const isEditing =
      editingCell?.productId === product.id && editingCell?.field === col;
    const isSaving = savingCell === cellKey(product.id, col);
    const displayValue = product[col] ?? '';

    if (isEditing) {
      if (col === 'description') {
        return (
          <textarea
            className="admin-panel__edit-input admin-products__desc-edit"
            value={editValue}
            autoFocus
            disabled={isSaving}
            rows={4}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={() => saveEdit(product, col)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                cancelEdit();
              }
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                saveEdit(product, col);
              }
            }}
          />
        );
      }

      return (
        <input
          className="admin-panel__edit-input"
          type={NUMERIC_FIELDS.has(col) ? 'number' : 'text'}
          step={col === 'exw_price' ? 'any' : undefined}
          value={editValue}
          autoFocus
          disabled={isSaving}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={() => saveEdit(product, col)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              saveEdit(product, col);
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              cancelEdit();
            }
          }}
        />
      );
    }

    const isDescription = col === 'description';
    const previewValue = isDescription
      ? truncateDescription(displayValue)
      : displayValue;
    const isTruncated = isDescription && String(displayValue).length > DESCRIPTION_PREVIEW_MAX;

    return (
      <span
        className={
          isEditable
            ? `admin-panel__cell-value admin-panel__cell-value--editable${isDescription ? ' admin-products__desc-preview' : ''}`
            : 'admin-panel__cell-value'
        }
        title={
          isTruncated
            ? displayValue
            : isEditable
              ? 'Doble clic para editar'
              : undefined
        }
        onDoubleClick={() => startEdit(product, col)}
      >
        {previewValue}
      </span>
    );
  };

  return (
    <Container fluid className="admin-panel p-4">
      <div className="admin-panel__header">
        <div>
          <h2 className="mb-1">Productos</h2>
          <p className="text-muted mb-0">Catálogo de productos CRM. Doble clic en un campo para editarlo.</p>
        </div>
        <InputGroup className="admin-panel__search">
          <InputGroup.Text>
            <Search size={16} />
          </InputGroup.Text>
          <Form.Control
            type="search"
            placeholder="Buscar por nombre, descripción, id o cualquier campo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>
      </div>

      {loading && <p className="text-muted mt-4">Cargando productos...</p>}
      {!loading && error && <p className="text-danger mt-4">{error}</p>}

      {!loading && (
        <>
          <p className="admin-panel__count text-muted">
            {filteredProducts.length} de {products.length} productos
          </p>
          <div className="table-responsive admin-panel__table-wrap">
            <Table striped bordered hover size="sm" className="admin-panel__table">
              <thead>
                <tr>
                  {PRODUCT_COLUMNS.map((col) => (
                    <th key={col} title={col}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredProducts.length === 0 ? (
                  <tr>
                    <td colSpan={PRODUCT_COLUMNS.length} className="text-center text-muted">
                      No se encontraron productos.
                    </td>
                  </tr>
                ) : (
                  filteredProducts.map((product) => (
                    <tr key={product.id}>
                      {PRODUCT_COLUMNS.map((col) => (
                        <td
                          key={col}
                          className={col === 'description' ? 'admin-products__desc-col' : undefined}
                        >
                          {renderCell(product, col)}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </>
      )}

      <button
        type="button"
        className="admin-products__fab"
        onClick={() => setShowCreateModal(true)}
        aria-label="Agregar producto"
      >
        +
      </button>

      {showCreateModal && (
        <div className="admin-products__backdrop" onClick={closeCreateModal}>
          <div className="admin-products__modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="admin-products__modal-close"
              onClick={closeCreateModal}
              aria-label="Cerrar"
            >
              ×
            </button>
            <h2 className="admin-products__modal-title">Nuevo producto</h2>

            <div className="admin-products__modal-field">
              <label>
                Nombre <span className="admin-products__required">*</span>
              </label>
              <input
                type="text"
                value={newProduct.name}
                onChange={(e) => setNewProduct((prev) => ({ ...prev, name: e.target.value }))}
                autoFocus
              />
            </div>

            <div className="admin-products__modal-field">
              <label>
                Descripción <span className="admin-products__required">*</span>
              </label>
              <textarea
                value={newProduct.description}
                onChange={(e) => setNewProduct((prev) => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <div className="admin-products__modal-field">
              <label>EXW Price</label>
              <input
                type="number"
                step="any"
                value={newProduct.exw_price}
                onChange={(e) => setNewProduct((prev) => ({ ...prev, exw_price: e.target.value }))}
              />
            </div>

            <div className="admin-products__modal-field">
              <label>Cost</label>
              <input
                type="number"
                value={newProduct.cost}
                onChange={(e) => setNewProduct((prev) => ({ ...prev, cost: e.target.value }))}
              />
            </div>

            <div className="admin-products__modal-field">
              <label>Estado (prod_status)</label>
              <input
                type="number"
                value={newProduct.prod_status}
                onChange={(e) => setNewProduct((prev) => ({ ...prev, prod_status: e.target.value }))}
              />
            </div>

            <button
              type="button"
              className="admin-products__modal-action"
              onClick={handleCreate}
              disabled={creating || !newProduct.name.trim() || !newProduct.description.trim()}
            >
              {creating ? 'Creando...' : 'Crear producto'}
            </button>
          </div>
        </div>
      )}
    </Container>
  );
}

export default Admin_products;
